import { useCallback, useEffect } from "react"
import { useReactFlow } from "@xyflow/react"

/**
 * Ensures the ReactFlow canvas has correct dimensions after mount and resizes.
 * ReactFlow sometimes mounts before its container has its final height (e.g. after Turbo navigation),
 * which can leave the canvas sized to only the visible/top portion.
 */
export default function useRelayout() {
  const reactFlowInstance = useReactFlow()

  const scheduleRelayout = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          reactFlowInstance.fitView({ padding: 0.2, duration: 0 })
        } catch (_) {
          // ignore if instance isn't fully ready yet
        }
      })
    })
  }, [reactFlowInstance])

  useEffect(() => {
    scheduleRelayout()

    const onResize = () => scheduleRelayout()
    window.addEventListener("resize", onResize)

    // Also observe the canvas container (helps when flex/min-h-0 settles after navigation)
    const rootEl = document.querySelector("[data-react-flow-target='canvas']")
    let ro
    if (rootEl && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => scheduleRelayout())
      ro.observe(rootEl)
    }

    return () => {
      window.removeEventListener("resize", onResize)
      if (ro) ro.disconnect()
    }
  }, [scheduleRelayout])
}
